import { create } from 'zustand';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Shared cache for GET /api/document-preferences.
 *
 * - `fetch()` is a no-op when data is already loaded or a request is in-flight,
 *   de-duplicating calls from multiple Settings tabs / ThemeContext.
 * - `update(patch)` does optimistic PUT: merges the patch into local state
 *   immediately, rolls back on failure, and returns {success, error}.
 * - `reset()` clears state (call on logout so next user starts clean).
 */
export const useDocPrefsStore = create((set, get) => ({
  data: null,
  loading: false,
  error: null,
  _inflight: null,

  fetch: async (opts = {}) => {
    const { force = false } = opts;
    const state = get();
    if (!force && state.data) return state.data;
    if (state._inflight) return state._inflight;
    // Skip when we don't have an auth token yet — AuthProvider hydrates
    // it from localStorage on mount, but child components can `fetch()`
    // on their own `useEffect` before that runs. Firing pre-auth
    // produces harmless 403s in the console and (on some tenants) a
    // "Failed to load…" toast. Consumers already handle a null return
    // (the store stays uninitialised) and re-fetch on the next relevant
    // event (login success, tab focus, forced refetch after edit).
    if (typeof window !== 'undefined' && !window.localStorage.getItem('token')) {
      return null;
    }

    const p = (async () => {
      set({ loading: true, error: null });
      try {
        const res = await axios.get(`${API}/document-preferences`);
        set({ data: res.data, loading: false, _inflight: null });
        return res.data;
      } catch (err) {
        set({ loading: false, error: err, _inflight: null });
        throw err;
      }
    })();

    set({ _inflight: p });
    return p;
  },

  update: async (patch) => {
    const prev = get().data;
    // Optimistic merge
    if (prev) set({ data: { ...prev, ...patch } });
    try {
      await axios.put(`${API}/document-preferences`, patch);
      // Re-fetch to pick up server-computed fields (counters, timestamps)
      const res = await axios.get(`${API}/document-preferences`);
      set({ data: res.data });
      return { success: true };
    } catch (err) {
      // Rollback
      set({ data: prev });
      return { success: false, error: err };
    }
  },

  /** Merge server-returned data into the cache (after an upload, etc). */
  setData: (data) => set({ data }),

  /** Local-only merge (for draft/uncommitted form edits). */
  patch: (partial) => {
    const prev = get().data;
    set({ data: prev ? { ...prev, ...partial } : { ...partial } });
  },

  reset: () => set({ data: null, loading: false, error: null, _inflight: null }),
}));
